import type { Expr } from '../core/types';
import { rampAt, type Ramp } from './ramps';
import { SN } from './glsl';

/**
 * Break positions in normalised 0-1 ramp space.
 * Quantile follows the distribution; equal interval divides the range evenly.
 */
export function classBreaks(n: number, method: string, hist?: number[] | null): number[] {
  const k = Math.max(2, Math.min(9, n | 0));
  const out: number[] = [];
  if (method === 'quantile' && hist && hist.length) {
    const total = hist.reduce((a, b) => a + b, 0);
    if (total > 0) {
      let cum = 0, want = 1;
      for (let b = 0; b < hist.length && want < k; b++) {
        cum += hist[b]!;
        while (want < k && cum >= total * want / k) { out.push((b + 1) / hist.length); want++; }
      }
      while (out.length < k - 1) out.push(out.length ? out[out.length - 1]! : 0.5);
      return out.slice(0, k - 1);
    }
  }
  for (let j = 1; j < k; j++) out.push(j / k);
  return out;
}

/** One colour per class, sampled at class midpoints so the ramp still reads. */
export function classColours(stops: Ramp['stops'], n: number): [number, number, number][] {
  const k = Math.max(2, Math.min(9, n | 0));
  const out: [number, number, number][] = [];
  for (let j = 0; j < k; j++) out.push(rampAt(stops, (j + 0.5) / k));
  return out;
}

/** A `case` chain per colour channel, turning the ramp into a step function. */
export function chanClass(
  t: Expr, stops: Ramp['stops'], breaks: number[], n: number, k: number,
): Expr {
  const cols = classColours(stops, n);
  const args: Expr[] = [];
  breaks.forEach((b, j) => {
    args.push(['<', t, SN(b, j / (n || 1))], cols[j]![k]!);
  });
  args.push(cols[cols.length - 1]![k]!);
  return ['case', ...args];
}
